import React, { useState } from 'react';
import AddCommentForm from './AddCommentForm';
import { MessageCircle, Star, ChevronDown, Flag, X } from 'lucide-react';
import { userServiceApi } from '@/services/api';
import { useCommentsByBook } from '../hooks/useCommentsByBook';
import type { Comment } from '../api/comments';

interface BookCommentsSectionProps {
  bookId: number;
}

const BookCommentsSection: React.FC<BookCommentsSectionProps> = ({ bookId }) => {
  const [mode, setMode] = useState<'list' | 'add'>('list');
  const [replyTo, setReplyTo] = useState<Comment | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const { data: comments, isLoading, error } = useCommentsByBook(bookId, { enabled: mode === 'list' });
  const showSuccessToast = (msg: string) => {
    setToastMsg(msg);
    window.setTimeout(() => setToastMsg(null), 3000);
  };

  // Report dialog state
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportForId, setReportForId] = useState<number | null>(null);
  const [reportToast, setReportToast] = useState<string | null>(null);
  const [reportErrorMsg, setReportErrorMsg] = useState<string | null>(null);

  const openReportDialog = (commentId: number) => {
    setReportForId(commentId);
    setReportReason('');
    setReportErrorMsg(null);
    setReportOpen(true);
  };

  const closeReportDialog = () => {
    setReportOpen(false);
  };

  const submitReport = async () => {
    if (!reportForId) return;
    const reason = reportReason.trim();
    if (!reason) {
      setReportErrorMsg('پر کردن این بخش الزامی است');
      return;
    }
    if (reason.length < 50) {
      setReportErrorMsg('حداقل ۵۰ کاراکتر برای توضیح دلیل گزارش وارد کنید');
      return;
    }
    setReportSubmitting(true);
    try {
      const res = await userServiceApi.post('/reports/', {
        target_type: 'comment',
        target_id: String(reportForId),
        reason,
      });
      if (res.success) {
        setReportOpen(false);
        setReportErrorMsg(null);
        setReportToast('گزارش شما ارسال شد');
        window.setTimeout(() => setReportToast(null), 3000);
      } else {
        // API returned success: false, show error from response
        console.log('API returned error:', res.error);
        setReportErrorMsg(res.error || 'خطا در ارسال گزارش');
      }
    } finally {
      setReportSubmitting(false);
    }
  };

  // Function to count total comments including replies
  const getTotalCommentCount = (comments: Comment[]): number => {
    let count = 0;
    comments.forEach(comment => {
      count++; // Count the main comment
      if (comment.replies && comment.replies.length > 0) {
        count += getTotalCommentCount(comment.replies); // Recursively count replies
      }
    });
    return count;
  };

  // Function to render star rating
  const renderStarRating = (rating: number) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating 
                ? 'fill-orange-400 text-orange-400' 
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  // Function to format Persian (Jalali) date exactly as: `24 شهریور 1404`
  const formatPersianDate = (dateString?: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    try {
      const parts = new Intl.DateTimeFormat('fa-IR-u-ca-persian-nu-latn', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
      }).formatToParts(date);
      const day = parts.find(p => p.type === 'day')?.value || '';
      const month = parts.find(p => p.type === 'month')?.value || '';
      const year = parts.find(p => p.type === 'year')?.value || '';
      if (day && month && year) return `${Number(day)} ${month} ${year}`;
      return parts.map(p => p.value).join(' ');
    } catch {
      return '';
    }
  };

  // Avatar helper
  const Avatar: React.FC<{ src?: string; alt: string }> = ({ src, alt }) => (
    <img
      src={src || 'https://api.dicebear.com/7.x/avataaars/svg?seed=guest&backgroundColor=f3f4f6&clothesColor=9ca3af'}
      alt={alt}
      className="w-12 h-12 rounded-full object-cover border border-gray-200"
      loading="lazy"
    />
  );

  // Recursive Comment Item
  const CommentItem: React.FC<{ comment: Comment; level?: number }> = ({ comment, level = 0 }) => {
    const [expanded, setExpanded] = useState(false);
    const indentClass = level > 0 ? 'pr-6 border-r-2 border-gray-100' : '';
    return (
      <div className={`bg-white rounded-lg p-6 border border-gray-100 ${level > 0 ? 'mt-3' : ''}`}>
        {/* Header with user info and rating */}
        <div className={`flex items-start justify-between mb-4 ${indentClass}`}>
          <div className="flex items-center gap-3">
            <Avatar src={comment.avatar_url} alt={comment.name || `کاربر ${comment.user_id}`} />
            <div>
              <h4 className="font-bold text-xs md:text-sm text-foreground font-persian mb-1">
                {comment.name || `کاربر ${comment.user_id}`}
              </h4>
              {renderStarRating(comment.rating)}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Report button */}
            <button
              type="button"
              title="گزارش"
              onClick={() => openReportDialog(comment.review_id)}
              className="p-1.5 rounded-full hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors"
            >
              <Flag className="w-5 h-5" />
            </button>
            <div className="text-[10px] md:text-xs text-muted-foreground font-persian whitespace-nowrap">
              {formatPersianDate(comment.created_at) || ''}
            </div>
          </div>
        </div>

        {/* Comment Text */}
        <div className={`${indentClass}`}>
          <p className="text-xs md:text-sm text-foreground leading-relaxed font-persian mb-4 whitespace-pre-line">
            {comment.comment}
          </p>

          {/* Action Buttons + Replies Toggle */}
          <div className="flex items-center gap-4">
            <button
              className="flex items-center gap-2 text-muted-foreground hover:text-blue-500 transition-colors"
              onClick={() => {
                setReplyTo(comment);
                setMode('add');
              }}
            >
              <MessageCircle className="w-4 h-4" />
              <span className="text-xs md:text-sm font-persian">پاسخ</span>
            </button>
            {/* Toggle replies (only show if there are replies) */}
            {Array.isArray(comment.replies) && comment.replies.length > 0 && (
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="ml-auto flex items-center gap-2 text-[#7063ff] hover:opacity-80 transition-opacity"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                <span className="text-xs md:text-sm font-persian">دیدن پاسخ ها</span>
              </button>
            )}
          </div>

          {/* Replies (collapsible) */}
          {Array.isArray(comment.replies) && comment.replies.length > 0 && (
            <div
              className={`mt-4 space-y-3 overflow-hidden transition-all duration-300 ${
                expanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              {comment.replies.map((rep) => (
                <CommentItem key={rep.review_id} comment={rep} level={level + 1} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f5f7fa' }} dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground font-persian">در حال بارگذاری نظرات...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f5f7fa' }} dir="rtl">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-muted-foreground font-persian">خطا در بارگذاری نظرات</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f5f7fa' }} dir="rtl">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-sm md:text-xl font-bold text-foreground font-persian mb-2">
            نظرات کاربران
          </h1>
          <p className="text-xs md:text-sm text-muted-foreground font-persian">
            تا الان {comments ? getTotalCommentCount(comments) : 0} نظر به این کتاب داده شده است
          </p>
        </div>


        {mode === 'list' ? (
          <>
            <div className="mt-6">
              <button
                onClick={() => {
                  setReplyTo(null);
                  setMode('add');
                }}
                className="w-full py-3 bg-[#7063ff] text-white font-bold text-sm md:text-base rounded-lg hover:bg-opacity-90 transition-all shadow-lg font-persian"
              >
                ثبت نظر
              </button>
            </div>
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12 text-red-500 font-persian">
                <p>خطا در بارگذاری نظرات</p>
              </div>
            ) : comments && comments.length > 0 ? (
              <div className="space-y-4 mt-6">
                {comments.map((c: Comment) => (
                  <CommentItem key={c.review_id} comment={c} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">💬</div>
                <h3 className="text-sm md:text-base font-bold text-foreground font-persian mb-2">
                  هنوز نظری ثبت نشده
                </h3>
                <p className="text-xs md:text-sm text-muted-foreground font-persian">
                  اولین نفری باشید که نظر خود را در مورد این کتاب بیان می‌کند
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <AddCommentForm
              bookId={bookId}
              parentComment={replyTo ?? undefined}
              onCancel={() => setMode('list')}
              onSuccess={() => {
                setReplyTo(null);
                setMode('list');
                showSuccessToast('نظر شما با موفقیت ثبت شد');
              }}
            />
            {toastMsg && (
              <div 
                className="fixed left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow font-persian text-sm bg-green-600 text-white"
                style={{ top: 'calc(1rem + env(safe-area-inset-top))' }}
              >
                {toastMsg}
              </div>
            )}
          </>
        )}
      </div>

      {reportToast && (
        <div 
          className={`fixed left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow font-persian text-sm text-white ${
            reportToast.includes('گزارش شما ارسال شد') ? 'bg-green-600' : 'bg-red-600'
          }`}
          style={{ top: 'calc(4rem + env(safe-area-inset-top))' }}
        >
          {reportToast}
        </div>
      )}

      {/* Report Dialog */}
      {reportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm md:text-base font-bold">گزارش</h3>
              <button type="button" onClick={closeReportDialog} className="p-1 rounded hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-xs md:text-sm mb-2">علت گزارش</label>
              <textarea
                className="w-full border rounded-lg p-3 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="لطفا با جزئیات دلیل گزارش خودتون رو توضیح بدید"
                value={reportReason}
                onChange={(e) => {
                  setReportReason(e.target.value);
                  if (reportErrorMsg) setReportErrorMsg(null);
                }}
              />
              <div className="flex justify-between items-center mt-1">
                <div>
                  {reportReason.trim().length === 0 && (
                    <p className="text-xs text-red-500">پر کردن این بخش الزامی است</p>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {reportReason.length}/50 کاراکتر
                </p>
              </div>
              {reportErrorMsg && (
                <p className="mt-2 text-sm text-red-600 font-persian">{reportErrorMsg}</p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={closeReportDialog} className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200">انصراف</button>
              <button
                type="button"
                disabled={reportSubmitting || reportReason.trim().length < 50}
                onClick={submitReport}
                className={`px-4 py-2 rounded-lg text-white ${reportSubmitting || reportReason.trim().length < 50 ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {reportSubmitting ? 'در حال ارسال...' : 'ارسال'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookCommentsSection;
